/* eslint-disable max-lines, max-lines-per-function */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './ProjectInfoLookup.scss';
import dayjs from 'dayjs';
import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { PROJECT_INFO_LOOKUP } from '../../../Api/resources/endPoint';

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

const flattenMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.flatMap((menuItem) => (Array.isArray(menuItem?.items) ? menuItem.items : []))
    : []
);

const getFilterMenuItem = (tableConfiguration = [], field) => {
  const column = Array.isArray(tableConfiguration)
    ? tableConfiguration.find((item) => item?.field === field)
    : null;

  if (!column) return null;

  return flattenMenuItems(column.menuItems).find(
    (item) => item?.type === 'filter' && item?.field === field,
  ) || null;
};

const getSelectedOptionLabel = (
  selectedValue,
  { allowIdentifierFallback = true } = {},
) => {
  if (!selectedValue) return '';
  if (typeof selectedValue === 'string') return selectedValue.trim();
  if (typeof selectedValue === 'number') return allowIdentifierFallback ? `${selectedValue}` : '';

  if (isNonEmptyString(selectedValue.name)) return selectedValue.name.trim();
  if (isNonEmptyString(selectedValue.label)) return selectedValue.label.trim();
  if (!allowIdentifierFallback) return '';
  if (selectedValue.value !== undefined && selectedValue.value !== null) {
    return `${selectedValue.value}`.trim();
  }
  if (selectedValue.id !== undefined && selectedValue.id !== null) {
    return `${selectedValue.id}`.trim();
  }

  return '';
};

const getReadableSelectedLabel = (selectedValue = null) => {
  const selectedLabel = getSelectedOptionLabel(selectedValue, {
    allowIdentifierFallback: false,
  });

  if (!selectedLabel) {
    return '';
  }

  if (!selectedValue || typeof selectedValue !== 'object') {
    return /^\d+$/.test(selectedLabel) ? '' : selectedLabel;
  }

  const rawIdentifiers = [
    selectedValue.id,
    selectedValue.value,
  ]
    .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
    .map((value) => `${value}`.trim());

  return rawIdentifiers.includes(selectedLabel.trim()) ? '' : selectedLabel;
};

const cloneSelectedValues = (selectedValues = []) => (
  Array.isArray(selectedValues)
    ? selectedValues
      .filter(Boolean)
      .map((selectedValue) => (
        typeof selectedValue === 'object'
          ? { ...selectedValue }
          : {
            id: selectedValue,
            value: selectedValue,
            label: `${selectedValue}`,
            name: `${selectedValue}`,
          }
      ))
    : []
);

const getOptionLabelFromChoices = (selectedValue, options = []) => {
  if (!selectedValue || !Array.isArray(options) || options.length === 0) {
    return '';
  }

  const selectedOptionKeys = new Set(
    [
      selectedValue?.id,
      selectedValue?.value,
      selectedValue,
    ]
      .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
      .map((value) => `${value}`.trim()),
  );

  const matchedOption = options.find((option) => (
    [
      option?.id,
      option?.value,
    ]
      .filter((value) => value !== undefined && value !== null && `${value}`.trim() !== '')
      .map((value) => `${value}`.trim())
      .some((value) => selectedOptionKeys.has(value))
  ));

  return getSelectedOptionLabel(matchedOption, { allowIdentifierFallback: false });
};

const getProjectNameFromFilterItem = (filterItem = null) => {
  if (!filterItem || typeof filterItem !== 'object') {
    return '';
  }

  const selectedValue = filterItem.selectedValue
    || filterItem.selectedValues?.[0];

  return getReadableSelectedLabel(selectedValue)
    || getOptionLabelFromChoices(selectedValue, filterItem.options);
};

const parseRequestPayload = (response = {}) => {
  const requestData = response?.config?.data;

  if (!requestData) return {};

  if (typeof requestData === 'string') {
    try {
      return JSON.parse(requestData);
    } catch {
      return {};
    }
  }

  return typeof requestData === 'object' ? requestData : {};
};

const getProjectNameFromRequestPayload = (response = {}) => {
  const requestPayload = parseRequestPayload(response);
  const payload = requestPayload?.payload;
  const candidateContainers = [
    requestPayload?.filter,
    requestPayload?.filters,
    requestPayload?.filterCriteria,
    requestPayload?.additionalPayloadData,
    payload?.filter,
    payload?.filters,
    payload?.filterCriteria,
    payload?.additionalPayloadData,
    payload,
    requestPayload,
  ].filter(Boolean);

  const projectNameCandidateValue = candidateContainers
    .map((container) => container?.projectName)
    .find(Boolean);
  const projectNameCandidate = Array.isArray(projectNameCandidateValue)
    ? projectNameCandidateValue[0]
    : projectNameCandidateValue;

  if (!projectNameCandidate) {
    return '';
  }

  if (projectNameCandidate?.selectedValue) {
    return getReadableSelectedLabel(projectNameCandidate.selectedValue)
      || getOptionLabelFromChoices(
        projectNameCandidate.selectedValue,
        projectNameCandidate.options,
      );
  }

  if (Array.isArray(projectNameCandidate?.selectedValues)) {
    return getReadableSelectedLabel(projectNameCandidate.selectedValues[0])
      || getOptionLabelFromChoices(
        projectNameCandidate.selectedValues[0],
        projectNameCandidate.options,
      );
  }

  return getReadableSelectedLabel(projectNameCandidate);
};

const getReportTitleFromResponse = (response = {}) => {
  const responseData = response?.data && typeof response.data === 'object' ? response.data : {};
  const payload = responseData?.data && typeof responseData.data === 'object'
    ? responseData.data
    : responseData;
  const reportTitle = `${payload.reportTitle || payload.title || ''}`.trim();

  return reportTitle.startsWith('Project Info Lookup - ') ? reportTitle : '';
};

const getResponsePayload = (response = {}) => {
  const responseData = response?.data && typeof response.data === 'object' ? response.data : {};

  return responseData?.data && typeof responseData.data === 'object'
    ? responseData.data
    : responseData;
};

const areEqualByValue = (leftValue, rightValue) => (
  JSON.stringify(leftValue) === JSON.stringify(rightValue)
);

const getReportTitleFromConfiguration = (
  response,
  tableConfiguration = [],
  defaultTitle = 'Project Info Lookup',
) => {
  const selectedProjectFilter = getFilterMenuItem(tableConfiguration, 'projectName');
  const responseReportTitle = getReportTitleFromResponse(response);
  const projectName = getProjectNameFromReportTitle(responseReportTitle)
    || getProjectNameFromRequestPayload(response)
    || getProjectNameFromFilterItem(selectedProjectFilter);

  return projectName ? `Project Info Lookup - ${projectName}` : defaultTitle;
};

const getAppliedFilterPayload = (tableConfiguration = []) => {
  const projectVertical = getFilterMenuItem(tableConfiguration, 'projectVertical');
  const projectType = getFilterMenuItem(tableConfiguration, 'projectType');
  const projectStatus = getFilterMenuItem(tableConfiguration, 'projectStatus');
  const projectName = getFilterMenuItem(tableConfiguration, 'projectName');
  const activity = getFilterMenuItem(tableConfiguration, 'activity');
  const total = getFilterMenuItem(tableConfiguration, 'total');
  const dateRange = getFilterMenuItem(tableConfiguration, 'dateRange');

  return {
    projectVertical: cloneSelectedValues(projectVertical?.selectedValues),
    projectType: cloneSelectedValues(projectType?.selectedValues),
    projectStatus: cloneSelectedValues(projectStatus?.selectedValues),
    projectName: {
      selectedValues: cloneSelectedValues(projectName?.selectedValues),
    },
    activity: cloneSelectedValues(activity?.selectedValues),
    total: {
      range: Array.isArray(total?.range) ? [...total.range] : [],
    },
    dateRange: {
      range: Array.isArray(dateRange?.range) ? [...dateRange.range] : [],
    },
  };
};

const getProjectNameFromAppliedFilters = (filterPayload = {}) => {
  const projectNameFilter = filterPayload?.projectName;
  const selectedProject = projectNameFilter?.selectedValues?.[0]
    || projectNameFilter?.selectedValue
    || projectNameFilter;

  return getReadableSelectedLabel(selectedProject)
    || getOptionLabelFromChoices(selectedProject, projectNameFilter?.options);
};

const sanitizeExportFileSegment = (value, fallback = 'Report') => {
  const normalizedValue = `${value ?? ''}`
    .trim()
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalizedValue || fallback;
};

const formatExportTimestamp = (date = new Date()) => {
  const reportDate = dayjs(date);
  return reportDate.format('DD-MMM-YYYY');
};

const getProjectNameFromExportFilters = (filters = {}) => {
  if (Array.isArray(filters)) {
    const projectNameFilter = filters.find((filterItem) => filterItem?.field === 'projectName');
    return getReadableSelectedLabel(projectNameFilter?.selectedValue)
      || getSelectedOptionLabel(projectNameFilter?.selectedValue, {
        allowIdentifierFallback: false,
      });
  }

  return getReadableSelectedLabel(filters?.projectName)
    || getSelectedOptionLabel(filters?.projectName, {
      allowIdentifierFallback: false,
    });
};

const buildActivityExportFileName = (exportContext = {}) => {
  const projectName = exportContext?.projectName
    || getProjectNameFromExportFilters(exportContext?.filters)
    || getProjectNameFromAppliedFilters(exportContext?.generalSettings?.additionalPayloadData);

  return `Project_info - ${sanitizeExportFileSegment(projectName)} - ${formatExportTimestamp()}`;
};

const buildActivityExportSheetName = (projectName = '') => (
  `Project_info - ${sanitizeExportFileSegment(projectName)}`
);

const getProjectNameFromReportTitle = (reportTitle = '') => {
  const titleParts = `${reportTitle}`.split(' - ');
  return titleParts.length > 1
    ? titleParts.slice(1).join(' - ')
      .trim()
    : '';
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;

  const toolbarButtons = rootNode.querySelectorAll('.button-group button');
  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isDownloadButton = !isFilterButton
      && !isResetButton
      && Boolean(buttonElement.querySelector('svg'));
    buttonElement.classList.toggle('project-info-lookup-reset-btn', isResetButton);
    buttonElement.classList.toggle('project-info-lookup-export-btn', isDownloadButton);
  });
};

const DEFAULT_REPORT_TITLE = 'Project Info Lookup';
const DEFAULT_PAGINATION = Object.freeze({
  page: 1,
  limit: 10,
  total: 0,
});
const TABLE_SCROLL_RATIO = 0.7;
const SUMMARY_ROW_ID = 'summary-row';
const INDIAN_NUMBER_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const REPORT_DATE_HEADER_PATTERN
  = /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+(\d{2}-[A-Za-z]{3}-\d{4})$/;

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${PROJECT_INFO_LOOKUP}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${PROJECT_INFO_LOOKUP}`;
};

const baseURL = fetchBaseURL();

const getColumnHeaderText = (column = {}) => {
  const headerCandidates = [
    column?.header,
    column?.label,
    column?.filterLabel,
    column?.menuLabel,
    column?.displayLabel,
    column?.field,
  ];

  const headerText = headerCandidates.find((candidate) => typeof candidate === 'string');

  return headerText ? headerText.trim() : '';
};

const isActivityDisplayColumn = (column = {}) => column?.field === 'activity';

const isTotalEffortDisplayColumn = (column = {}) => {
  const headerText = getColumnHeaderText(column).toLowerCase();

  return column?.field === 'total' || headerText.startsWith('total effort');
};

const isDateDisplayColumn = (column = {}) => REPORT_DATE_HEADER_PATTERN.test(
  getColumnHeaderText(column),
);

const getNumericDisplayFields = (tableConfiguration = []) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration
      .filter((column) => isTotalEffortDisplayColumn(column) || isDateDisplayColumn(column))
      .map((column) => column?.field)
      .filter(Boolean)
    : []
);

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return 0;
  }

  const isNegativeValue = trimmedValue.startsWith('(') && trimmedValue.endsWith(')');
  const normalizedValue = trimmedValue.replace(/,/g, '').replace(/[()%]/g, '');
  const parsedValue = Number(normalizedValue);

  if (!Number.isFinite(parsedValue)) {
    return 0;
  }

  return isNegativeValue ? -parsedValue : parsedValue;
};

const formatIndianDecimal = (value) => INDIAN_NUMBER_FORMATTER.format(parseNumericValue(value));

const formatNumericFieldsInRow = (row = {}, numericFields = []) => {
  if (!row || typeof row !== 'object') {
    return row;
  }

  const formattedRow = { ...row };

  numericFields.forEach((field) => {
    formattedRow[field] = formatIndianDecimal(row[field]);
  });

  if (Array.isArray(row.children)) {
    formattedRow.children = row.children.map((childRow) => formatNumericFieldsInRow(
      childRow,
      numericFields,
    ));
  }

  return formattedRow;
};

const formatTableData = (tableData = [], numericFields = []) => (
  Array.isArray(tableData)
    ? tableData.map((row) => formatNumericFieldsInRow(row, numericFields))
    : []
);

const renderStackedHeader = (
  primaryLabel,
  secondaryLabel,
  variant = '',
  tertiaryLabel = '',
) => (
  <span className={`project-info-lookup-header-stack ${variant}`.trim()}>
    <span className="activity-report-header-primary">{primaryLabel}</span>
    <span className="activity-report-header-secondary">{secondaryLabel}</span>
    {tertiaryLabel ? (
      <span className="activity-report-header-tertiary">{tertiaryLabel}</span>
    ) : null}
  </span>
);

const buildDisplayHeader = (column = {}) => {
  const headerText = getColumnHeaderText(column);

  if (isTotalEffortDisplayColumn(column)) {
    return renderStackedHeader(
      'Total effort',
      '(hours)',
      'project-info-lookup-header-stack--numeric',
    );
  }

  const dateHeaderMatch = headerText.match(REPORT_DATE_HEADER_PATTERN);

  if (dateHeaderMatch) {
    const [, weekDay, dayLabel] = dateHeaderMatch;

    if (column?.isHoliday) {
      return renderStackedHeader(
        'Holiday',
        weekDay,
        'project-info-lookup-header-stack--numeric',
        dayLabel,
      );
    }

    return renderStackedHeader(
      weekDay,
      dayLabel,
      'project-info-lookup-header-stack--numeric',
    );
  }

  return headerText;
};

const removeInlineSortIcon = (menuItems = [], shouldHideSortIcon = false) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => (
          item?.type === 'sort' && shouldHideSortIcon
            ? {
              ...item,
              showSortButtonInHeader: false,
            }
            : item
        ))
        : menuItem?.items,
    }))
    : []
);

const normalizeFilterSelectionValue = (selectedValue) => {
  if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
    return null;
  }

  if (typeof selectedValue === 'object' && !Array.isArray(selectedValue)) {
    return selectedValue.value
      ?? selectedValue.id
      ?? selectedValue.name
      ?? selectedValue.label
      ?? null;
  }

  return selectedValue;
};

const normalizeMultiSelectMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') {
            return item;
          }

          if (item?.template !== 'multiSelect' && item?.template !== 'choice') {
            return item;
          }

          const normalizedSelectedValues = Array.isArray(item?.selectedValues)
            ? item.selectedValues
              .map((selectedValue) => {
                if (item.field !== 'projectName') {
                  return normalizeFilterSelectionValue(selectedValue);
                }

                if (selectedValue && typeof selectedValue === 'object') {
                  return selectedValue;
                }

                const selectedKey = normalizeFilterSelectionValue(selectedValue);
                return item.options?.find((option) => (
                  `${option?.id ?? option?.value}` === `${selectedKey}`
                )) || selectedValue;
              })
              .filter((selectedValue) => selectedValue !== null)
            : item?.selectedValues;

          return {
            ...item,
            selectedValues: normalizedSelectedValues,
          };
        })
        : menuItem?.items,
    }))
    : []
);

const applyActivityReportFilterMetadata = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') {
            return item;
          }

          if (item?.field === 'dateRange') {
            return {
              ...item,
              tableUtilityConfig: {
                ...(item?.tableUtilityConfig || {}),
                maxRangeDays: 31,
                maxRangeValidationMessage: 'Date range has to be within 31 days.',
              },
            };
          }

          return item;
        })
        : menuItem?.items,
    }))
    : []
);

const ensureMenuItems = (tableConfiguration = []) => (
  Array.isArray(tableConfiguration)
    ? tableConfiguration.map((column) => ({
      ...column,
      menuItems: Array.isArray(column?.menuItems) ? column.menuItems : [],
    }))
    : []
);

const transformTableConfiguration = (tableConfiguration = []) => ensureMenuItems(
  tableConfiguration,
).map((column) => {
  const headerText = getColumnHeaderText(column);
  const shouldShowHeaderActions = false;
  const shouldDefaultPinColumn = isActivityDisplayColumn(column);
  const shouldShowPinButton = false;
  const hideInlineSortIcon
    = shouldShowHeaderActions;

  return {
    ...column,
    filter: shouldShowHeaderActions ? true : column?.filter,
    pinnable: shouldShowHeaderActions ? false : column?.pinnable,
    defaultPinned: shouldShowHeaderActions
      ? shouldDefaultPinColumn
      : column?.defaultPinned,
    isPinned: shouldShowHeaderActions
      ? false
      : column?.isPinned,
    filterVisible: shouldShowHeaderActions
      ? false
      : column?.filterVisible,
    pinVisible: shouldShowHeaderActions
      ? shouldShowPinButton
      : column?.pinVisible,
    header: buildDisplayHeader({
      ...column,
      header: headerText,
    }),
    label: column?.label || headerText,
    filterLabel: column?.filterLabel || headerText,
    menuLabel: column?.menuLabel || headerText,
    displayLabel: column?.displayLabel || headerText,
    menuItems: removeInlineSortIcon(
      applyActivityReportFilterMetadata(
        normalizeMultiSelectMenuItems(column?.menuItems),
      ),
      hideInlineSortIcon,
    ),
  };
});

const buildSummaryRow = (summary = null) => {
  if (!summary || typeof summary !== 'object' || Object.keys(summary).length === 0) {
    return null;
  }

  return {
    ...summary,
    id: summary.id || SUMMARY_ROW_ID,
    rowConfiguration: {
      ...(summary.rowConfiguration || {}),
      isFrozen: true,
      backgroundColor: summary.rowConfiguration?.backgroundColor || '#eceff5',
    },
  };
};

const syncPaginationFromResponse = (setPagination, response) => {
  const meta = response?.meta && typeof response.meta === 'object' ? response.meta : {};

  setPagination((previousPagination) => ({
    ...previousPagination,
    page: Number(meta.page) > 0 ? Number(meta.page) : previousPagination.page,
    limit: Number(meta.limit) > 0 ? Number(meta.limit) : previousPagination.limit,
    total: Number(meta.total) >= 0 ? Number(meta.total) : previousPagination.total,
  }));
};

const applyActivityReportHeaderClass = (rootNode) => {
  if (!rootNode) return;

  const headers = rootNode.querySelectorAll('.project-overview-header');
  headers.forEach((headerElement) => {
    headerElement.classList.add('project-info-lookup-header');
  });
};

const applyActivityTableViewport = (rootNode) => {
  if (!rootNode) return;

  const tableContainers = rootNode.querySelectorAll('.table-container');
  tableContainers.forEach((tableContainerElement) => {
    tableContainerElement.classList.add('project-info-lookup-table-viewport');
  });
};

const applyBodyCellHoverTitles = (rootNode) => {
  if (!rootNode) return;

  const bodyCells = rootNode.querySelectorAll(
    '.project-info-lookup-table-utility .p-datatable-tbody > tr > td',
  );

  bodyCells.forEach((td) => {
    const contentElement
      = td.querySelector('.cell-wrapper .ellipses')
        || td.querySelector('.cell-wrapper > span')
        || td.querySelector('span')
        || td.querySelector('div')
        || td;

    const fullText = (contentElement.textContent || '').trim();
    const isTruncated = contentElement.scrollWidth > contentElement.clientWidth;

    if (fullText && isTruncated) {
      td.setAttribute('title', fullText);
      contentElement.setAttribute('title', fullText);
      return;
    }

    td.removeAttribute('title');
    if (contentElement !== td) {
      contentElement.removeAttribute('title');
    }
  });
};

const useActivityReportTableEffects = ({
  containerRef,
  setTableScrollHeight,
}) => {
  useEffect(() => {
    const rootNode = containerRef.current;

    const updateTableClasses = () => {
      applyActivityTableViewport(rootNode);
      applyActivityReportHeaderClass(rootNode);
      applyBodyCellHoverTitles(rootNode);
      applyToolbarButtonClasses(rootNode);
    };

    const timer = setTimeout(updateTableClasses, 500);
    const observer = new MutationObserver(updateTableClasses);
    const tableContainer = rootNode?.querySelector('.project-info-lookup-table-utility');

    if (tableContainer) {
      observer.observe(tableContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    const handleResize = () => {
      setTableScrollHeight(`${Math.floor(window.innerHeight * TABLE_SCROLL_RATIO)}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [containerRef, setTableScrollHeight]);
};

const useProjectNameTitleSync = ({
  appliedFilterPayload,
  projectNameFilterUrl,
  accessToken,
  utilityUserInfo,
  setReportTitle,
  projectOptionsRef,
}) => {
  useEffect(() => {
    const selectedProject = appliedFilterPayload?.projectName;
    const selectedProjectId = selectedProject?.id
      ?? selectedProject?.value
      ?? selectedProject?.selectedValues?.[0]
      ?? null;
    const selectedProjectName = `${selectedProject?.name || selectedProject?.label || ''}`.trim();

    if (!selectedProjectId || !projectNameFilterUrl || !accessToken) {
      return undefined;
    }

    if (selectedProjectName && selectedProjectName !== `${selectedProjectId}`.trim()) {
      setReportTitle(`Project Info Lookup - ${selectedProjectName}`);
      return undefined;
    }

    let isMounted = true;

    const fetchProjectName = async () => {
      try {
        const response = await fetch(projectNameFilterUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            token: accessToken,
          },
          body: JSON.stringify({
            payload: {
              searchQuery: '',
              userInfo: utilityUserInfo,
              ...appliedFilterPayload,
            },
          }),
        });

        const responsePayload = await response.json();
        const projectOptions = Array.isArray(responsePayload?.data) ? responsePayload.data : [];
        if (isMounted && projectOptionsRef) {
          projectOptionsRef.current = projectOptions;
        }

        const matchedProject = projectOptions.find((projectOption) => (
          [`${projectOption?.id ?? ''}`.trim(), `${projectOption?.value ?? ''}`.trim()]
            .includes(`${selectedProjectId}`.trim())
        ));
        const matchedProjectName = `${matchedProject?.name || matchedProject?.label || ''}`.trim();

        if (isMounted && matchedProjectName) {
          setReportTitle(`Project Info Lookup - ${matchedProjectName}`);
        }
      } catch {
        // Keep the current title if the lookup endpoint fails.
      }
    };

    fetchProjectName();

    return () => {
      isMounted = false;
    };
  }, [
    accessToken,
    appliedFilterPayload,
    projectNameFilterUrl,
    setReportTitle,
    utilityUserInfo,
  ]);
};

const ProjectInfoLookup = () => {
  const { userProfile } = useAuth();
  const containerRef = useRef(null);

  const [tableScrollHeight, setTableScrollHeight] = useState('400px');
  const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [appliedFilterPayload, setAppliedFilterPayload] = useState({});
  const [projectNameFilterUrl, setProjectNameFilterUrl] = useState('');
  const projectOptionsRef = useRef([]);

  const utilityUserInfo = useMemo(() => ({
    emailId: userProfile?.email,
    permissions: userProfile?.permissions,
    roleDetails: userProfile?.roleDetails,
    employeeId: userProfile?.employeeId,
    globalColors: userProfile?.siteConfiguration?.global_colors,
  }), [userProfile]);

  useActivityReportTableEffects({
    containerRef,
    setTableScrollHeight,
  });

  useProjectNameTitleSync({
    appliedFilterPayload,
    projectNameFilterUrl,
    accessToken: userProfile?.access_token,
    utilityUserInfo,
    setReportTitle,
    projectOptionsRef,
  });

  const handleFilteredApiResponse = useCallback((response) => {
    const payload = getResponsePayload(response);
    // Inject project options dynamically via a getter to resolve numeric IDs to names in "Filters applied" drawer.
    // Since TableUtility manages its own state for tableConfiguration, using a getter ensures that
    // when the AppliedFiltersDrawer reads `col.options`, it always gets the most up-to-date options from the ref.
    const tableConfiguration = transformTableConfiguration(payload.tableConfiguration).map((col) => {
      if (col.field === 'projectName') {
        const enhancedCol = { ...col };
        Object.defineProperty(enhancedCol, 'options', {
          get: () => projectOptionsRef.current || [],
          enumerable: true,
        });
        return enhancedCol;
      }
      return col;
    });

    const numericDisplayFields = getNumericDisplayFields(tableConfiguration);
    const rawTableData = Array.isArray(payload.tableData) ? payload.tableData : [];
    const formattedTableData = formatTableData(rawTableData, numericDisplayFields);
    const formattedSummary = formatNumericFieldsInRow(payload.summary, numericDisplayFields);
    const summaryRow = formattedTableData.length > 0 ? buildSummaryRow(formattedSummary) : null;
    const tableData = summaryRow ? [...formattedTableData, summaryRow] : formattedTableData;

    response.data = {
      ...(response?.data || {}),
      tableConfiguration,
      tableData,
      data: {
        ...payload,
        tableConfiguration,
        tableData,
      },
    };

    const nextAppliedFilterPayload = getAppliedFilterPayload(tableConfiguration);
    const projectNameFilterItem = getFilterMenuItem(tableConfiguration, 'projectName');
    const nextReportTitle = getReportTitleFromConfiguration(response, tableConfiguration);
    const nextProjectNameFilterUrl = projectNameFilterItem?.url || '';

    setReportTitle((previousTitle) => (
      previousTitle === nextReportTitle ? previousTitle : nextReportTitle
    ));
    setAppliedFilterPayload((previousPayload) => (
      areEqualByValue(previousPayload, nextAppliedFilterPayload)
        ? previousPayload
        : nextAppliedFilterPayload
    ));
    setProjectNameFilterUrl((previousUrl) => (
      previousUrl === nextProjectNameFilterUrl ? previousUrl : nextProjectNameFilterUrl
    ));
    syncPaginationFromResponse(setPagination, response);
  }, []);

  const handlePaginationChange = useCallback((nextPagination) => {
    setPagination(nextPagination);
  }, []);

  const buildSelectedProjectExportFileName = useCallback((exportContext = {}) => {
    const selectedProjectName = getProjectNameFromReportTitle(reportTitle)
      || getProjectNameFromAppliedFilters(appliedFilterPayload)
      || getProjectNameFromExportFilters(exportContext?.filters);

    return buildActivityExportFileName({
      ...exportContext,
      projectName: selectedProjectName,
      generalSettings: {
        ...exportContext?.generalSettings,
        additionalPayloadData: appliedFilterPayload,
      },
    });
  }, [appliedFilterPayload, reportTitle]);

  const buildSelectedProjectExportSheetName = useCallback((exportContext = {}) => {
    const selectedProjectName = getProjectNameFromReportTitle(reportTitle)
      || getProjectNameFromAppliedFilters(appliedFilterPayload)
      || getProjectNameFromExportFilters(exportContext?.filters)
      || 'Report';

    return buildActivityExportSheetName(selectedProjectName);
  }, [appliedFilterPayload, reportTitle]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      userInfo: utilityUserInfo,
      apiEndpoints: {
        getAllViews: {
          endpoint: '',
          enabled: false,
        },
        getFilteredData: {
          endpoint: '',
          enabled: true,
        },
        saveOrUpdateView: {
          endpoint: '',
          enabled: false,
        },
        deleteView: {
          endpoint: '',
          enabled: false,
        },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: buildSelectedProjectExportFileName,
          sheetName: buildSelectedProjectExportSheetName,
          reportTitle: buildSelectedProjectExportFileName,
        },
      },
      handler: {
        handleFilteredApiResponse,
      },
      additionalPayloadData: {},
      headers: {
        token: userProfile?.access_token,
      },
      showToolBar: true,
      showPagination: true,
      toolBarSettings: {
        showFilterOption: true,
        showAddNew: false,
        showDownloadExcel: true,
        downloadExcelFileName: buildSelectedProjectExportFileName,
        downloadExcelSheetName: buildSelectedProjectExportSheetName,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showToolBarHeading: true,
        toolBarHeading: reportTitle,
        showSaveView: false,
        showReset: false,
        showExpandCollapse: false,
      },
    },
    tableSettings: {
      size: 'small',
      pageSize: pagination.limit,
      loaderSkeleton: {
        rows: pagination.limit,
        bodyCells: 2,
        columns: 5,
      },
      paginationConfig: {
        ...pagination,
        onPaginationChange: handlePaginationChange,
      },
      showExpandCollapse: false,
      enableTreeTable: false,
      tableScrollHeight,
      maxFrozenColumns: {
        desktop: 1,
        tablet: 1,
        mobile: 1,
      },
    },
  }), []);

  if (!userProfile?.access_token) {
    return null;
  }

  return (
    <div className="project-info-lookup-outer-layer" ref={containerRef}>
      <div className="project-info-lookup-table-container">
        <div className="project-info-lookup-table-utility">
          <TableUtility
            configuration={configuration}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectInfoLookup;
